/**
 * Which log level a non-ok HTTP response should use in the dashboard API client.
 *
 * fetchApi's non-ok branch HANDLES three statuses with a dedicated side effect:
 *   401 -> session-expiry broadcast (maybeNotifySessionExpired) / signup bootstrap race
 *   403 -> plan/trial upgrade modal (upgradeInterceptor)
 *   404 -> caller recovery (useProjectNotFoundRedirect and siblings)
 *
 * These are expected, user-visible-handled outcomes, NOT crashes. log.error
 * reaches console.error in prod, which PostHog capture_console_errors files as a
 * $exception -- so a HANDLED 404 renders as a fake "crash" in Error Tracking.
 *
 * Incident 2026-07-13: a real signup deleted the seeded AURA demo while its
 * /issues tab sat open. The redirect (4051fa60) bounced them off the dead route,
 * but every re-mount still logged the 404 at error and filed 2-3 $exceptions --
 * 3 days of fake-crash noise for one user across 07-13..07-16. This is the same
 * class already fixed for network failures (client.ts warns + captures
 * api_network_failure instead of erroring); it was never applied to the
 * HTTP-error branch.
 *
 * Handled statuses log at warn (logger.ts gates warn behind DEBUG_OVERLAYQA, so
 * prod stays quiet but a debugging user can still see them). Genuinely unexpected
 * statuses (5xx, unhandled 4xx like 400/409/422) stay at log.error so real
 * regressions remain prod-visible from a user screenshot (investigation 2026-07-07).
 */
const HANDLED_HTTP_STATUSES: ReadonlySet<number> = new Set([401, 403, 404]);

export function isHandledHttpStatus(status: number): boolean {
  return HANDLED_HTTP_STATUSES.has(status);
}

/**
 * Whether a response status should log at warn rather than error, given a
 * per-request opt-in.
 *
 * A caller passes `expectedErrorStatuses` (via FetchOptions) for statuses IT
 * owns and surfaces itself, so its refusal is not a crash. Example: the
 * share-create form owns its 400 ("cannot share with yourself"); the server
 * refusal is expected and the form renders the message inline, so filing it as a
 * $exception is noise. The opt-in stays scoped to that one caller — 400 is NOT
 * added to HANDLED_HTTP_STATUSES, so every other 400 across the app stays loud
 * and a real regression is still prod-visible.
 */
export function isHandledResponse(
  status: number,
  expectedErrorStatuses?: readonly number[],
): boolean {
  return isHandledHttpStatus(status) || (expectedErrorStatuses?.includes(status) ?? false);
}
